import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
  Tab,
  Tabs,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { parseISO, format } from "date-fns";
import Select from "react-select";
import SidebarPanelNavigation from "../../../components/SidebarPanelNavigation";
import LoadingIndicator from "../../../components/LoadingIndicator";
import TopProductsBarChart from "../../../components/TopProductsBarChart";
import useNotification from "../../../contexts/notification";
import CustomMessageModal from "../../../components/CustomMessageModal";
import useAuth from "../../../contexts/auth";
import {
  createLead,
  deleteLead,
  getLeadRequestsGroupedByCategory,
  getLeadRequestsGroupedByProduct,
  getLeads,
  getPromocoes,
  importLeads,
  updateLead,
  enviarPromocoesParaLead,
  enviarTemplateParaLead,
  enviarTemplateParaLeads,
  enviarMensagemPersonalizada,

} from "../../../services/api";

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  try {
    return format(parseISO(value), "dd/MM/yyyy HH:mm");
  } catch (error) {
    return "-";
  }
}

function sanitizePhone(phone) {
  return phone ? phone.replace(/\D+/g, "") : "";
}

function parseImportedLeads(text = "") {
  const leads = [];
  const skipped = [];
  const lines = text.split(/\r?\n/);

  lines.forEach((line, index) => {
    const trimmed = (line || "").trim();

    if (!trimmed) {
      return;
    }

    const phoneMatch = trimmed.match(/(\+?\d[\d\s().-]{8,})/);

    if (!phoneMatch) {
      skipped.push({ index, line: trimmed, reason: "Nenhum número encontrado" });
      return;
    }

    const phoneDigits = phoneMatch[0].replace(/\D+/g, "");

    if (phoneDigits.length < 10) {
      skipped.push({ index, line: trimmed, reason: "Número muito curto" });
      return;
    }

    const nameCandidate = trimmed
      .replace(phoneMatch[0], "")
      .replace(/[;,\t]+/g, " ")
      .trim();

    leads.push({
      phone: phoneDigits,
      name: nameCandidate || null,
    });
  });

  const uniqueLeads = [];
  const seenPhones = new Set();

  leads.forEach((lead) => {
    if (seenPhones.has(lead.phone)) {
      skipped.push({
        index: null,
        line: lead.phone,
        reason: "Número duplicado",
      });
      return;
    }

    seenPhones.add(lead.phone);
    uniqueLeads.push(lead);
  });

  return { leads: uniqueLeads, skipped };
}

function getLeadLookupKeys(lead = {}) {
  const keys = [];

  if (lead == null || typeof lead !== "object") {
    return keys;
  }

  if (lead.id !== undefined && lead.id !== null) {
    keys.push(String(lead.id));
  }

  const sanitizedPhone = sanitizePhone(lead.phone);
  if (sanitizedPhone) {
    keys.push(sanitizedPhone);

    const normalizedName = (lead.name || "").trim().toLowerCase();
    if (normalizedName) {
      keys.push(`${sanitizedPhone}-${normalizedName}`);
    }
  }

  return keys;
}

function appendLeadPreference(map, lead, value) {
  if (!value) {
    return;
  }

  const keys = getLeadLookupKeys(lead);

  if (keys.length === 0) {
    return;
  }

  keys.forEach((key) => {
    if (!map.has(key)) {
      map.set(key, new Set());
    }
    map.get(key).add(value);
  });
}

function collectLeadPreferences(map, lead) {
  const result = new Set();
  const keys = getLeadLookupKeys(lead);

  keys.forEach((key) => {
    const values = map.get(key);
    if (values) {
      values.forEach((entry) => result.add(entry));
    }
  });

  return result;
}

function LeadModal({
  show,
  onHide,
  onSubmit,
  saving,
  initialData,
  promotions,
}) {
  const [formValues, setFormValues] = useState({
    name: "",
    phone: "",
    interesse: false,
    blacklist: false,
    promocoes: [],
  });

  useEffect(() => {
    if (initialData) {
      setFormValues({
        name: initialData.name || "",
        phone: initialData.phone || "",
        interesse: Boolean(initialData.interesse),
        blacklist: Boolean(initialData.blacklist),
        promocoes: (initialData.promocoes || []).map((promo) => promo.id),
      });
    } else {
      setFormValues({
        name: "",
        phone: "",
        interesse: false,
        blacklist: false,
        promocoes: [],
      });
    }
  }, [initialData, show]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...formValues,
      phone: sanitizePhone(formValues.phone),
    });
  };

  const handleMultiSelectChange = (event) => {
    const values = Array.from(event.target.selectedOptions).map((option) =>
      Number(option.value)
    );
    setFormValues((prev) => ({ ...prev, promocoes: values }));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {initialData ? "Editar Lead" : "Adicionar Lead"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>Nome (opcional)</Form.Label>
              <Form.Control
                value={formValues.name}
                onChange={(event) =>
                  setFormValues((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder="Nome do lead"
              />
              <Form.Text className="text-muted">
                Caso não seja informado, usaremos o número como referência.
              </Form.Text>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>Telefone *</Form.Label>
              <Form.Control
                value={formValues.phone}
                onChange={(event) =>
                  setFormValues((prev) => ({ ...prev, phone: event.target.value }))
                }
                placeholder="Ex.: (85) 99999-0000"
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Check
                type="switch"
                id="lead-interesse-switch"
                label="Interessado em receber promoções"
                checked={formValues.interesse}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    interesse: event.target.checked,
                    blacklist: event.target.checked ? false : prev.blacklist,
                  }))
                }
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Check
                type="switch"
                id="lead-blacklist-switch"
                label="Blacklist"
                checked={formValues.blacklist}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    blacklist: event.target.checked,
                    interesse: event.target.checked ? false : prev.interesse,
                  }))
                }
              />
            </Col>
            <Col xs={12}>
              <Form.Label>Promoções vinculadas</Form.Label>
              <Form.Control
                as="select"
                multiple
                value={formValues.promocoes}
                onChange={handleMultiSelectChange}
              >
                {promotions.map((promo) => (
                  <option key={promo.id} value={promo.id}>
                    {promo.nome}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                O lead receberá automaticamente as promoções associadas quando
                estiver dentro da janela de 24h após interagir.
              </Form.Text>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ImportLeadsModal({ show, onHide, onSubmit, loading }) {
  const [rawList, setRawList] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!show) {
      setRawList("");
      setLocalError("");
    }
  }, [show]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const { leads, skipped } = parseImportedLeads(rawList);

    if (leads.length === 0) {
      setLocalError("Adicione pelo menos um número válido para importar.");
      return;
    }

    setLocalError("");
    onSubmit(leads, skipped);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Importar lista de leads</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Cole uma lista com um número por linha. Se preferir, inclua nome e número na mesma linha
            (por exemplo: "Ana; (85) 99999-0000" ou "(85) 99999-0000 - João"). Ignoramos linhas vazias e
            removemos números duplicados automaticamente.
          </p>
          {localError && (
            <Alert variant="danger">{localError}</Alert>
          )}
          <Form.Label>Lista de contatos</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={rawList}
            onChange={(event) => setRawList(event.target.value)}
            placeholder={"Ex.:\n(85) 98888-7777 - Maria\nJoão; 11999990000"}
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Números com menos de 10 dígitos serão ignorados. Usaremos o número como nome quando o campo não
            for informado.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Importando..." : "Importar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default function LeadsAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.type === "admin";
  const isPoster = user?.type === "poster";
  const allowed = isAdmin || isPoster;
  const [loading, setLoading] = useState(true);
  const [savingLead, setSavingLead] = useState(false);
  const [leads, setLeads] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [search, setSearch] = useState("");
  const [interestSearch, setInterestSearch] = useState("");
  const [interestFilter, setInterestFilter] = useState("all");
  const [blacklistFilter, setBlacklistFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [templateMessageFilter, setTemplateMessageFilter] = useState("pending");
  const [customMessageFilter, setCustomMessageFilter] = useState("pending");
  const [promotionMessageFilter, setPromotionMessageFilter] = useState("pending");
  const [interactionFilter, setInteractionFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [bulkTemplateSending, setBulkTemplateSending] = useState(false);
  const [bulkPromotionSending, setBulkPromotionSending] = useState(false);
  const [templateBatchSize, setTemplateBatchSize] = useState(1000);
  const [customMessageModal, setCustomMessageModal] = useState({
    open: false,
    leadIds: [],
    contextLabel: "",
    type: "single",
  });
  const [customMessageSending, setCustomMessageSending] = useState(false);
  const [importingLeads, setImportingLeads] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const selectAllRef = useRef(null);
  const { setMessage } = useNotification();

  const loadData = useCallback(async () => {
    if (!allowed) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [categoriesResponse, productsResponse, leadsResponse, promotionsResponse] =
        await Promise.all([
          getLeadRequestsGroupedByCategory(),
          getLeadRequestsGroupedByProduct(),
          isAdmin ? getLeads() : Promise.resolve([]),
          isAdmin ? getPromocoes() : Promise.resolve([]),
        ]);

      setCategoryGroups(Array.isArray(categoriesResponse) ? categoriesResponse : []);
      setProductGroups(Array.isArray(productsResponse) ? productsResponse : []);
      setLeads(Array.isArray(leadsResponse) ? leadsResponse : []);
      setPromotions(Array.isArray(promotionsResponse) ? promotionsResponse : []);
    } catch (error) {
      setCategoryGroups([]);
      setProductGroups([]);
      setLeads([]);
      setPromotions([]);
      setMessage({
        error: true,
        content: "Não foi possível carregar os dados de leads. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  }, [allowed, isAdmin, setMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const categorySummaries = useMemo(() => {
    return categoryGroups
      .map((group) => {
        const leadsMap = new Map();

        (group?.requests || []).forEach((request) => {
          const lead = request?.lead || {};
          const leadKey =
            lead.id || `${lead.phone || ""}-${lead.name || "lead"}` ||
            `request-${request?.id}`;

          if (!leadsMap.has(leadKey)) {
            leadsMap.set(leadKey, {
              id: lead.id || leadKey,
              name: lead.name || "Lead",
              phone: lead.phone || "",
              products: [],
              latestRequestAt: null,
            });
          }

          const entry = leadsMap.get(leadKey);

          if (request?.product_name) {
            entry.products.push(request.product_name);
          }

          if (request?.requested_at) {
            const timestamp = Date.parse(request.requested_at);

            if (!Number.isNaN(timestamp)) {
              if (!entry.latestRequestAt || timestamp > entry.latestRequestAt) {
                entry.latestRequestAt = timestamp;
              }
            }
          }
        });

        const leadsList = Array.from(leadsMap.values())
          .map((entry) => ({
            ...entry,
            products: Array.from(new Set(entry.products)),
            latestRequestAt: entry.latestRequestAt
              ? new Date(entry.latestRequestAt).toISOString()
              : null,
          }))
          .sort((a, b) =>
            a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
          );

        return {
          category: group?.category || "Não categorizado",
          totalLeads:
            typeof group?.total_leads === "number"
              ? group.total_leads
              : leadsList.length,
          leads: leadsList,
        };
      })
      .sort((a, b) => {
        if (b.totalLeads !== a.totalLeads) {
          return b.totalLeads - a.totalLeads;
        }

        return a.category.localeCompare(b.category, "pt-BR", { sensitivity: "base" });
      });
  }, [categoryGroups]);

  const productInterestSummaries = useMemo(() => {
    return productGroups
      .map((group) => {
        const leadsList = (group?.interested_leads || [])
          .map((lead) => ({
            id: lead?.id || `${lead?.phone || ""}-${lead?.name || "Lead"}`,
            name: lead?.name || "Lead",
            phone: lead?.phone || "",
            requestedAt: lead?.requested_at || null,
          }))
          .sort((a, b) =>
            a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
          );

        return {
          productName: group?.product_name || "Produto sem nome",
          category: group?.category || "Não categorizado",
          totalLeads:
            typeof group?.total_leads === "number"
              ? group.total_leads
              : leadsList.length,
          totalRequests:
            typeof group?.total_requests === "number"
              ? group.total_requests
              : leadsList.length,
          leads: leadsList,
        };
      })
      .sort((a, b) => {
        if (b.totalLeads !== a.totalLeads) {
          return b.totalLeads - a.totalLeads;
        }

        return a.productName.localeCompare(b.productName, "pt-BR", {
          sensitivity: "base",
        });
      });
  }, [productGroups]);

  const leadCategoryMap = useMemo(() => {
    const map = new Map();

    categorySummaries.forEach((group) => {
      group.leads.forEach((lead) => {
        appendLeadPreference(map, lead, group.category);
      });
    });

    return map;
  }, [categorySummaries]);

  const leadProductMap = useMemo(() => {
    const map = new Map();

    productInterestSummaries.forEach((group) => {
      group.leads.forEach((lead) => {
        appendLeadPreference(map, lead, group.productName);
      });
    });

    return map;
  }, [productInterestSummaries]);
  const leadById = useMemo(() => {
    const map = new Map();

    leads.forEach((lead) => {
      if (lead && lead.id !== undefined && lead.id !== null) {
        map.set(lead.id, lead);
      }
    });

    return map;
  }, [leads]);
  const categoryOptions = useMemo(() => {
    const uniqueCategories = new Set(
      categorySummaries.map((group) => group.category)
    );

    return Array.from(uniqueCategories).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
  }, [categorySummaries]);
  const categorySelectOptions = useMemo(
    () => [
      { value: "all", label: "Todas" },
      ...categoryOptions.map((category) => ({
        value: category,
        label: category,
      })),
    ],
    [categoryOptions]
  );
  const selectedCategoryOption = useMemo(
    () =>
      categorySelectOptions.find((option) => option.value === categoryFilter) ||
      categorySelectOptions[0],
    [categoryFilter, categorySelectOptions]
  );

  const productOptions = useMemo(() => {
    const uniqueProducts = new Set(
      productInterestSummaries.map((group) => group.productName)
    );

    return Array.from(uniqueProducts).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
  }, [productInterestSummaries]);
  const productSelectOptions = useMemo(
    () => [
      { value: "all", label: "Todos" },
      ...productOptions.map((product) => ({ value: product, label: product })),
    ],
    [productOptions]
  );
  const selectedProductOption = useMemo(
    () =>
      productSelectOptions.find((option) => option.value === productFilter) ||
      productSelectOptions[0],
    [productFilter, productSelectOptions]
  );

  const filteredLeads = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const normalizedInterestQuery = interestSearch.trim().toLowerCase();

    return leads.filter((lead) => {
      const matchesSearch = normalizedSearch
        ? [lead.name, lead.phone]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedSearch))
        : true;

      const productRequests =
        lead.productRequests || lead.product_requests || [];

      const interestSources = [
        lead.interesse_texto,
        ...productRequests.map((request) => request.productName || request.product_name),
        ...productRequests.map((request) => request.category),
      ].filter(Boolean);

      const matchesInterestQuery = normalizedInterestQuery
        ? interestSources.some((value) =>
          value.toLowerCase().includes(normalizedInterestQuery)
        )
        : true;

      const matchesInterest =
        interestFilter === "all"
          ? true
          : lead.interesse === (interestFilter === "interested");

      const matchesBlacklist =
        blacklistFilter === "all"
          ? true
          : lead.blacklist === (blacklistFilter === "blacklist");

      const leadCategories = collectLeadPreferences(leadCategoryMap, lead);
      const matchesCategory =
        categoryFilter === "all"
          ? true
          : leadCategories.has(categoryFilter);

      const leadProducts = collectLeadPreferences(leadProductMap, lead);
      const matchesProduct =
        productFilter === "all"
          ? true
          : leadProducts.has(productFilter);

      const hasCustomMessage = Boolean(lead.last_custom_message_sent_at);
      const matchesCustomMessage =
        customMessageFilter === "all"
          ? true
          : customMessageFilter === "sent"
            ? hasCustomMessage
            : !hasCustomMessage;

      const hasTemplateMessage = Boolean(lead.last_template_sent_at);
      const matchesTemplateMessage =
        templateMessageFilter === "all"
          ? true
          : templateMessageFilter === "sent"
            ? hasTemplateMessage
            : !hasTemplateMessage;

      const hasPromotionMessage = Boolean(lead.last_promotion_sent_at);
      const matchesPromotionMessage =
        promotionMessageFilter === "all"
          ? true
          : promotionMessageFilter === "sent"
            ? hasPromotionMessage
            : !hasPromotionMessage;

      const hasInteraction = Boolean(lead.last_interaction_at);
      const matchesInteraction =
        interactionFilter === "all"
          ? true
          : interactionFilter === "interacted"
            ? hasInteraction
            : !hasInteraction;

      return (
        matchesSearch &&
        matchesInterestQuery &&
        matchesInterest &&
        matchesBlacklist &&
        matchesCategory &&
        matchesProduct &&
        matchesTemplateMessage &&
        matchesCustomMessage &&
        matchesPromotionMessage &&
        matchesInteraction
      );
    });
  }, [
    leads,
    search,
    interestSearch,
    interestFilter,
    blacklistFilter,
    categoryFilter,
    productFilter,
    templateMessageFilter,
    leadCategoryMap,
    leadProductMap,
    customMessageFilter,
    promotionMessageFilter,
    interactionFilter,
  ]);

  const filteredLeadIdSet = useMemo(() => {
    return new Set(filteredLeads.map((lead) => lead.id).filter(Boolean));
  }, [filteredLeads]);

  const selectedVisibleLeadIds = useMemo(() => {
    return selectedLeadIds.filter((id) => filteredLeadIdSet.has(id));
  }, [selectedLeadIds, filteredLeadIdSet]);

  const allFilteredSelected =
    filteredLeadIdSet.size > 0 &&
    selectedVisibleLeadIds.length === filteredLeadIdSet.size;

  useEffect(() => {
    if (!selectAllRef.current) {
      return;
    }

    selectAllRef.current.indeterminate =
      selectedVisibleLeadIds.length > 0 && !allFilteredSelected;
  }, [selectedVisibleLeadIds.length, allFilteredSelected]);

  useEffect(() => {
    setSelectedLeadIds((prev) =>
      prev.filter((id) => leads.some((lead) => lead.id === id))
    );
  }, [leads]);

  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return Array.from(next);
    });
  };

  const handleToggleSelectAll = () => {
    if (filteredLeadIdSet.size === 0) {
      return;
    }

    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      const currentlySelected = Array.from(filteredLeadIdSet).filter((id) =>
        next.has(id)
      ).length;
      const shouldSelectAll = currentlySelected !== filteredLeadIdSet.size;

      if (shouldSelectAll) {
        filteredLeadIdSet.forEach((id) => next.add(id));
      } else {
        filteredLeadIdSet.forEach((id) => next.delete(id));
      }

      return Array.from(next);
    });
  };

  const handleClearSelection = () => {
    if (selectedVisibleLeadIds.length === 0) {
      return;
    }

    setSelectedLeadIds((prev) =>
      prev.filter((id) => !filteredLeadIdSet.has(id))
    );
  };

  const runBulkAction = async ({
    leadIds,
    action,
    setLoading,
    successMessage,
    errorMessage,
  }) => {
    if (leadIds.length === 0) {
      setMessage({
        error: true,
        content: "Selecione ao menos um lead da lista filtrada para continuar.",
      });
      return;
    }

    setLoading(true);

    const failures = [];

    for (const id of leadIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await action(id);
      } catch (error) {
        failures.push(id);
      }
    }

    if (failures.length === 0) {
      setMessage({
        error: false,
        content: successMessage(leadIds.length),
      });
    } else if (failures.length === leadIds.length) {
      setMessage({
        error: true,
        content: errorMessage,
      });
    } else {
      setMessage({
        error: true,
        content:
          "Não foi possível concluir o envio para todos os leads selecionados. Tente novamente.",
      });
    }

    setLoading(false);
  };

  const totalProductRequests = useMemo(() => {
    return categoryGroups.reduce((total, group) => {
      const groupTotal = group?.total_requests;
      if (typeof groupTotal === "number") {
        return total + groupTotal;
      }

      return total + (group?.requests?.length || 0);
    }, 0);
  }, [categoryGroups]);

  const leadsWithRequests = useMemo(() => {
    const ids = new Set();

    categoryGroups.forEach((group) => {
      (group?.requests || []).forEach((request) => {
        const lead = request?.lead;

        if (lead?.id) {
          ids.add(lead.id);
          return;
        }

        const phone = lead?.phone || "";
        const name = lead?.name || "lead";
        ids.add(`${phone}-${name}`);
      });
    });

    return ids.size;
  }, [categoryGroups]);

  const topProductStats = useMemo(() => {
    return productGroups
      .map((group) => {
        const productName = (group?.product_name || group?.productName || "")
          .trim()
          .replace(/\s+/g, " ");

        if (!productName) {
          return null;
        }

        const totalRequests =
          typeof group?.total_requests === "number"
            ? group.total_requests
            : Array.isArray(group?.requests)
              ? group.requests.length
              : 0;

        return {
          product: productName,
          count: totalRequests,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [productGroups]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLead(null);
  };

  const handleCloseImportModal = () => {
    if (importingLeads) {
      return;
    }

    setShowImportModal(false);
  };

  const handleSubmitLead = async (values) => {
    setSavingLead(true);
    try {
      if (editingLead) {
        await updateLead(editingLead.id, values);
        setMessage({
          error: false,
          content: "Lead atualizado com sucesso.",
        });
      } else {
        await createLead(values);
        setMessage({ error: false, content: "Lead criado com sucesso." });
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      setMessage({
        error: true,
        content: "Não foi possível salvar o lead. Verifique os dados e tente novamente.",
      });
    } finally {
      setSavingLead(false);
    }
  };

  const handleImportLeadsFromList = async (parsedLeads, skippedRows = []) => {
    setImportingLeads(true);

    try {
      const response = await importLeads({ leads: parsedLeads });

      const importedCount = response?.imported ?? parsedLeads.length;
      const apiSkipped = Array.isArray(response?.skipped) ? response.skipped : [];
      const totalSkipped = apiSkipped.length + (skippedRows?.length || 0);
      const skippedLabel = totalSkipped > 0
        ? ` (${totalSkipped} linha${totalSkipped === 1 ? "" : "s"} ignorada${totalSkipped === 1 ? "" : "s"})`
        : "";
      const contentMessage = `${importedCount} lead${importedCount === 1 ? "" : "s"} importado${importedCount === 1 ? "" : "s"}${skippedLabel}.`;

      setMessage({
        error: false,
        content: contentMessage,
      });

      setShowImportModal(false);
      loadData();
    } catch (error) {
      const apiMessage = error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        "Não foi possível importar os leads. Tente novamente.";

      setMessage({
        error: true,
        content: apiMessage,
      });
    } finally {
      setImportingLeads(false);
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este lead?")) {
      return;
    }

    try {
      await deleteLead(id);
      setMessage({ error: false, content: "Lead removido com sucesso." });
      loadData();
    } catch (error) {
      setMessage({
        error: true,
        content: "Não foi possível remover o lead. Tente novamente.",
      });
    }
  };

  const handleSendPromotions = async (leadId) => {
    try {
      const response = await enviarPromocoesParaLead(leadId);
      setMessage({
        error: false,
        content: response?.status || "Promoções disparadas com sucesso.",
      });
    } catch (error) {
      setMessage({
        error: true,
        content:
          "Não foi possível disparar as promoções para este lead. Verifique se ele está dentro da janela de 24h.",
      });
    }
  };

  const handleSendTemplate = async (leadId) => {
    try {
      const response = await enviarTemplateParaLead(leadId);
      setMessage({
        error: false,
        content: response?.status || "Template enviado com sucesso.",
      });
    } catch (error) {
      const apiMessage = error?.response?.data?.mensagem;

      setMessage({
        error: true,
        content:
          apiMessage || "Não foi possível enviar o template para este lead. Tente novamente.",
      });
    }
  };

  const totalLeads = leads.length;
  const interestedCount = leads.filter((lead) => lead.interesse).length;
  const blacklistCount = leads.filter((lead) => lead.blacklist).length;
  const recentInteractions = leads.filter((lead) => lead.last_interaction_at).length;
  const selectedCount = selectedVisibleLeadIds.length;

  const renderLeadPreferences = (lead) => {
    const categories = Array.from(
      collectLeadPreferences(leadCategoryMap, lead)
    ).sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));
    const products = Array.from(collectLeadPreferences(leadProductMap, lead)).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );

    if (categories.length === 0 && products.length === 0) {
      return <span className="text-muted">Sem preferências registradas</span>;
    }

    return (
      <div>
        {categories.length > 0 && (
          <div className="mb-2">
            <small className="text-muted text-uppercase d-block">Categorias</small>
            <div className="d-flex flex-wrap">
              {categories.map((category) => (
                <Badge
                  key={`${lead.id}-cat-${category}`}
                  variant="primary"
                  className="mr-1 mb-1"
                >
                  {category}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {products.length > 0 && (
          <div>
            <small className="text-muted text-uppercase d-block">Produtos</small>
            <div className="d-flex flex-wrap">
              {products.map((product) => (
                <Badge
                  key={`${lead.id}-prod-${product}`}
                  variant="info"
                  className="mr-1 mb-1"
                >
                  {product}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMessageStatus = (lead) => {
    const badges = [];

    if (lead.last_template_sent_at) {
      badges.push({
        key: "template",
        label: `Template: ${formatDateTime(lead.last_template_sent_at)}`,
        variant: "secondary",
      });
    }

    if (lead.last_custom_message_sent_at) {
      badges.push({
        key: "custom",
        label: `Personalizada: ${formatDateTime(lead.last_custom_message_sent_at)}`,
        variant: "info",
      });
    }

    if (lead.last_promotion_sent_at) {
      badges.push({
        key: "promotion",
        label: `Promoção: ${formatDateTime(lead.last_promotion_sent_at)}`,
        variant: "success",
      });
    }

    if (badges.length === 0) {
      return <span className="text-muted">Sem envios</span>;
    }

    return (
      <div className="d-flex flex-column">
        {badges.map((badge) => (
          <Badge
            key={`${lead.id}-${badge.key}`}
            variant={badge.variant}
            className="mb-1 text-left"
          >
            {badge.label}
          </Badge>
        ))}
      </div>
    );
  };

  const renderLeadInterest = (lead) => {
    const interestText = (lead.interesse_texto || "").trim();
    const productRequests = lead.productRequests || lead.product_requests || [];
    const requestLabels = productRequests
      .map(
        (request) =>
          request.product_name || request.productName || request.category || ""
      )
      .filter(Boolean);

    if (!interestText && requestLabels.length === 0) {
      return <span className="text-muted">Sem registro</span>;
    }

    const chips = interestText ? [interestText, ...requestLabels] : requestLabels;

    return (
      <div className="d-flex flex-wrap">
        {chips.map((text, index) => (
          <Badge
            key={`${lead.id}-interest-${index}-${text}`}
            variant="light"
            className="mr-1 mb-1 text-dark border"
          >
            {text}
          </Badge>
        ))}
      </div>
    );
  };

  const handleBulkSendTemplate = async () => {
    if (selectedVisibleLeadIds.length === 0) {
      setMessage({
        error: true,
        content: "Selecione ao menos um lead da lista filtrada para continuar.",
      });
      return;
    }

    setBulkTemplateSending(true);

    try {
      const shouldEnqueue = selectedVisibleLeadIds.length >= 50;
      const lote = Math.min(5000, Math.max(50, Number(templateBatchSize) || 500));
      setTemplateBatchSize(lote);
      const response = await enviarTemplateParaLeads({
        leadIds: selectedVisibleLeadIds,
        async: shouldEnqueue,
        lote,
      });

      const {
        total_enviados: totalEnviados = selectedVisibleLeadIds.length,
        total_falhas: totalFalhas = 0,
        total_lotes: totalLotes,
        processamento,
        total_leads: totalLeads,
      } = response || {};
      const leadsDestino = totalLeads || selectedVisibleLeadIds.length;
      const processadoNaFila = processamento?.tipo === "fila";

      if (processadoNaFila) {
        setMessage({
          error: false,
          content:
            totalLotes && totalLotes > 1
              ? `Templates enfileirados em ${totalLotes} lotes. O worker vai disparar para ${leadsDestino} leads sem travar o front-end.`
              : `Templates enfileirados. O worker vai disparar para ${leadsDestino} lead${leadsDestino === 1 ? "" : "s"}.`,
        });
        return;
      }

      if (totalFalhas === 0) {
        setMessage({
          error: false,
          content: `${totalEnviados} template${totalEnviados === 1 ? "" : "s"} enviado${totalEnviados === 1 ? "" : "s"} com sucesso.`,
        });
      } else if (totalFalhas === selectedVisibleLeadIds.length) {
        setMessage({
          error: true,
          content: "Não foi possível enviar o template para os leads selecionados.",
        });
      } else {
        setMessage({
          error: true,
          content: "Não foi possível concluir o envio para todos os leads selecionados. Tente novamente.",
        });
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.mensagem;
      setMessage({
        error: true,
        content: apiMessage || "Não foi possível enviar o template para os leads selecionados.",
      });
    } finally {
      setBulkTemplateSending(false);
    }
  };

  const handleBulkSendPromotions = () => {
    runBulkAction({
      leadIds: selectedVisibleLeadIds,
      action: enviarPromocoesParaLead,
      setLoading: setBulkPromotionSending,
      successMessage: (quantity) =>
        `${quantity} disparo${quantity === 1 ? "" : "s"} de promoção realizado${quantity === 1 ? "" : "s"
        } com sucesso.`,
      errorMessage:
        "Não foi possível disparar as promoções para os leads selecionados.",
    });
  };
  const handleCloseCustomMessageModal = () => {
    if (customMessageSending) {
      return;
    }

    setCustomMessageModal({
      open: false,
      leadIds: [],
      contextLabel: "",
      type: "single",
    });
  };

  const openCustomMessageModalForLead = (lead) => {
    if (!lead || !lead.id) {
      return;
    }

    if (lead.blacklist) {
      setMessage({
        error: true,
        content: "Este lead está na blacklist. Remova-o para enviar mensagens.",
      });
      return;
    }

    setCustomMessageModal({
      open: true,
      leadIds: [lead.id],
      contextLabel: lead.name || lead.phone || "Lead selecionado",
      type: "single",
    });
  };

  const handleOpenBulkCustomMessageModal = () => {
    const eligibleLeadIds = selectedVisibleLeadIds.filter((id) => {
      const lead = leadById.get(id);
      return lead && !lead.blacklist;
    });

    if (eligibleLeadIds.length === 0) {
      setMessage({
        error: true,
        content:
          "Selecione ao menos um lead liberado (fora da blacklist) para enviar mensagens personalizadas.",
      });
      return;
    }

    const label = eligibleLeadIds.length === 1
      ? leadById.get(eligibleLeadIds[0])?.name ||
      leadById.get(eligibleLeadIds[0])?.phone ||
      "Lead selecionado"
      : `${eligibleLeadIds.length} leads selecionados`;

    setCustomMessageModal({
      open: true,
      leadIds: eligibleLeadIds,
      contextLabel: label,
      type: eligibleLeadIds.length > 1 ? "bulk" : "single",
    });
  };

  const handleSubmitCustomMessage = async (text) => {
    const trimmed = (text || "").trim();

    if (trimmed.length < 3 || customMessageModal.leadIds.length === 0) {
      setMessage({
        error: true,
        content: "Informe uma mensagem com ao menos 3 caracteres.",
      });
      return;
    }

    setCustomMessageSending(true);

    try {
      const response = await enviarMensagemPersonalizada({
        leadIds: customMessageModal.leadIds,
        mensagem: trimmed,
      });

      const totalEnviados = response?.total_enviados ?? customMessageModal.leadIds.length;
      const totalFalhas = response?.total_falhas ?? 0;

      const falhasDetalhadas = Array.isArray(response?.falhas)
        ? response.falhas.slice(0, 3)
        : [];

      const detalhesFalha = falhasDetalhadas
        .map((falha) => `#${falha?.lead_id}: ${falha?.motivo}`)
        .filter(Boolean)
        .join(" | ");

      const suffix = detalhesFalha
        ? ` Detalhes: ${detalhesFalha}${(response?.falhas?.length || 0) > falhasDetalhadas.length ? "..." : ""}`
        : "";

      const content = totalFalhas > 0
        ? `${totalEnviados} mensagem${totalEnviados === 1 ? "" : "s"} enviada${totalEnviados === 1 ? "" : "s"}, ${totalFalhas} falha${totalFalhas === 1 ? "" : "s"} no envio.${suffix}`
        : totalEnviados === 1
          ? "Mensagem personalizada enviada com sucesso."
          : `${totalEnviados} mensagens personalizadas enviadas com sucesso.`;

      setMessage({
        error: totalFalhas > 0,
        content,
      });

      setCustomMessageModal({
        open: false,
        leadIds: [],
        contextLabel: "",
        type: "single",
      });

      await loadData();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        "Não foi possível enviar a mensagem personalizada. Tente novamente.";
      setMessage({
        error: true,
        content: apiMessage,
      });
    } finally {
      setCustomMessageSending(false);
    }
  };

  if (!allowed) {
    return <Redirect to="/" />;
  }

  return (
    <Container fluid>
      <div>
        <SidebarPanelNavigation isAdmin={isAdmin} />
        <div className="content-panel">
          {loading ? (
            <LoadingIndicator />
          ) : (
            <>
              <Row className="align-items-center">
                <Col md={8} className="mb-3">
                  <h2 className="mb-1">Gestão de Leads</h2>
                  <p className="text-muted mb-0">
                    Acompanhe os leads captados, vincule promoções e acompanhe a jornada de interesse em tempo real.
                  </p>
                </Col>
                <Col md={4} className="text-md-right text-sm-left mb-3">
                  {isAdmin && (
                    <div className="d-flex justify-content-md-end flex-wrap">
                      <Button
                        variant="outline-primary"
                        className="mr-2 mb-2"
                        onClick={() => setShowImportModal(true)}
                      >
                        Importar lista
                      </Button>
                      <Button className="mb-2" onClick={() => setShowModal(true)}>
                        Novo Lead
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>

              <Tabs
                id="leads-submenu"
                activeKey={activeSection}
                onSelect={(key) => setActiveSection(key || "overview")}
                className="mb-4 leads-submenu"
                justify
              >
                <Tab eventKey="overview" title="Visão geral">
                  <div className="pt-4">
                    {isAdmin && (
                      <Row className="my-3">
                        <Col xl={3} md={6} className="mb-3">
                          <Card>
                            <Card.Body>
                              <h3 className="text-primary mb-1">{totalLeads}</h3>
                              <span className="text-muted">Leads cadastrados</span>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col xl={3} md={6} className="mb-3">
                          <Card>
                            <Card.Body>
                              <h3 className="text-success mb-1">{interestedCount}</h3>
                              <span className="text-muted">Interessados ativos</span>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col xl={3} md={6} className="mb-3">
                          <Card>
                            <Card.Body>
                              <h3 className="text-danger mb-1">{blacklistCount}</h3>
                              <span className="text-muted">Blacklist</span>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col xl={3} md={6} className="mb-3">
                          <Card>
                            <Card.Body>
                              <h3 className="text-info mb-1">{recentInteractions}</h3>
                              <span className="text-muted">Interações recentes</span>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    <Row className="mb-3">
                      <Col xl={6} md={6} className="mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <h5 className="text-muted">Solicitações de produtos</h5>
                            <div className="d-flex align-items-baseline justify-content-between">
                              <h2 className="text-dark mb-0">{totalProductRequests}</h2>
                              <Badge variant="light">
                                {leadsWithRequests} lead{leadsWithRequests === 1 ? "" : "s"} únicos
                              </Badge>
                            </div>
                            <small className="text-muted d-block mt-2">
                              Contabiliza todos os pedidos recebidos via formulário, separados por categoria e produto.
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xl={6} md={6} className="mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <h5 className="text-muted">Produtos com maior interesse</h5>
                            {topProductStats.length === 0 ? (
                              <Alert variant="light" className="mb-0">
                                Ainda não há produtos com interesse registrado.
                              </Alert>
                            ) : (
                              <TopProductsBarChart products={topProductStats} />
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {isPoster && !isAdmin && (
                      <Card className="mb-3">
                        <Card.Body>
                          <h5 className="mb-2">Visualização de respostas</h5>
                          <p className="text-muted mb-0">
                            Você pode acompanhar todas as respostas recebidas organizadas por categoria e por produto.
                            Para editar contatos ou disparar mensagens, procure um administrador.
                          </p>
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="insights" title="Interesses e respostas">
                  <div className="pt-4">
                    <Row className="mb-3">
                      <Col xl={6} className="mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <Card.Title>Respostas por categoria</Card.Title>
                            <Card.Subtitle className="text-muted mb-3">
                              Veja quem solicitou cada tipo de produto com nomes e modelos desejados.
                            </Card.Subtitle>
                            {categorySummaries.length === 0 ? (
                              <Alert variant="light" className="mb-0">
                                Nenhum lead informou interesse por categoria até o momento.
                              </Alert>
                            ) : (
                              <div>
                                {categorySummaries.map((group) => (
                                  <div key={group.category} className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <h6 className="mb-0">{group.category}</h6>
                                      <Badge variant="primary">
                                        {group.totalLeads} lead{group.totalLeads === 1 ? "" : "s"}
                                      </Badge>
                                    </div>
                                    <Table size="sm" responsive className="mb-0">
                                      <thead>
                                        <tr>
                                          <th>Nome</th>
                                          <th>Produtos solicitados</th>
                                          <th>Última solicitação</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.leads.map((lead) => (
                                          <tr key={`${group.category}-${lead.id}`}>
                                            <td className="align-middle">{lead.name}</td>
                                            <td className="align-middle" style={{ minWidth: 180 }}>
                                              {lead.products.length > 0 ? lead.products.join(", ") : "-"}
                                            </td>
                                            <td className="align-middle">
                                              {lead.latestRequestAt ? formatDateTime(lead.latestRequestAt) : "-"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                ))}
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xl={6} className="mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <Card.Title>Produtos com interessados</Card.Title>
                            <Card.Subtitle className="text-muted mb-3">
                              Agrupamos os leads pelos itens solicitados para facilitar disparos direcionados.
                            </Card.Subtitle>
                            {productInterestSummaries.length === 0 ? (
                              <Alert variant="light" className="mb-0">
                                Ainda não há produtos solicitados pelos leads.
                              </Alert>
                            ) : (
                              <div>
                                {productInterestSummaries.map((group) => (
                                  <div key={group.productName} className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <h6 className="mb-0">{group.productName}</h6>
                                      <Badge variant={group.totalLeads > 1 ? "success" : "secondary"}>
                                        {group.totalLeads} interessado{group.totalLeads === 1 ? "" : "s"}
                                      </Badge>
                                    </div>
                                    <small className="d-block text-muted mb-2">
                                      Categoria: {group.category}
                                      {" · "}
                                      Solicitações registradas: {group.totalRequests}
                                    </small>
                                    <Table size="sm" responsive className="mb-0">
                                      <thead>
                                        <tr>
                                          <th>Nome</th>
                                          <th>Última solicitação</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.leads.map((lead) => (
                                          <tr key={`${group.productName}-${lead.id}`}>
                                            <td className="align-middle">{lead.name}</td>
                                            <td className="align-middle">
                                              {lead.requestedAt ? formatDateTime(lead.requestedAt) : "-"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                ))}
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </Tab>

                <Tab
                  eventKey="management"
                  title={isAdmin ? "Gestão e disparos" : "Lista de contatos"}
                >
                  <div className="pt-4">
                    {isAdmin ? (
                      <>
                        <Card className="mb-3">
                          <Card.Body>
                            <Row>
                              <Col lg={4} className="mb-3">
                                <Form.Label>Buscar</Form.Label>
                                <Form.Control
                                  placeholder="Nome ou telefone"
                                  value={search}
                                  onChange={(event) => setSearch(event.target.value)}
                                />
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Interesse (texto ou código)</Form.Label>
                                <Form.Control
                                  placeholder="Ex.: A56, iPhone 14, cupom"
                                  value={interestSearch}
                                  onChange={(event) => setInterestSearch(event.target.value)}
                                />
                                <Form.Text className="text-muted">
                                  Filtra pelo texto informado pelo lead ou pelas solicitações registradas.
                                </Form.Text>
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Interesse</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={interestFilter}
                                  onChange={(event) => setInterestFilter(event.target.value)}
                                >
                                  <option value="all">Todos</option>
                                  <option value="interested">Somente interessados</option>
                                  <option value="not-interested">Não interessados</option>
                                </Form.Control>
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Blacklist</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={blacklistFilter}
                                  onChange={(event) => setBlacklistFilter(event.target.value)}
                                >
                                  <option value="all">Todos</option>
                                  <option value="blacklist">Somente blacklist</option>
                                  <option value="released">Apenas liberados</option>
                                </Form.Control>
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Categoria de interesse</Form.Label>
                                <Select
                                  classNamePrefix="select"
                                  options={categorySelectOptions}
                                  value={selectedCategoryOption}
                                  onChange={(option) =>
                                    setCategoryFilter(option?.value || "all")
                                  }
                                  placeholder="Pesquisar categoria"
                                  isClearable
                                  noOptionsMessage={() => "Nenhuma categoria encontrada"}
                                />
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Produto desejado</Form.Label>
                                <Select
                                  classNamePrefix="select"
                                  options={productSelectOptions}
                                  value={selectedProductOption}
                                  onChange={(option) =>
                                    setProductFilter(option?.value || "all")
                                  }
                                  placeholder="Pesquisar produto"
                                  isClearable
                                  noOptionsMessage={() => "Nenhum produto encontrado"}
                                />
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Envio de template</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={templateMessageFilter}
                                  onChange={(event) =>
                                    setTemplateMessageFilter(event.target.value)
                                  }
                                >
                                  <option value="all">Todos</option>
                                  <option value="sent">Template enviado</option>
                                  <option value="pending">Sem template enviado</option>
                                </Form.Control>
                                <Form.Text className="text-muted">
                                  Começamos escondendo quem já recebeu o template para evitar duplicidade.
                                </Form.Text>
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Mensagens personalizadas</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={customMessageFilter}
                                  onChange={(event) => setCustomMessageFilter(event.target.value)}
                                >
                                  <option value="all">Todos</option>
                                  <option value="sent">Já receberam</option>
                                  <option value="pending">Aguardando envio</option>
                                </Form.Control>
                                <Form.Text className="text-muted">
                                  Por padrão, exibimos apenas quem ainda não recebeu mensagem personalizada.
                                </Form.Text>
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Disparos de promoção</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={promotionMessageFilter}
                                  onChange={(event) => setPromotionMessageFilter(event.target.value)}
                                >
                                  <option value="all">Todos</option>
                                  <option value="sent">Promoção enviada</option>
                                  <option value="pending">Sem promoção enviada</option>
                                </Form.Control>
                                <Form.Text className="text-muted">
                                  O filtro também começa ocultando quem já recebeu promoções para evitar duplicidade.
                                </Form.Text>
                              </Col>
                              <Col lg={4} md={6} className="mb-3">
                                <Form.Label>Interações registradas</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={interactionFilter}
                                  onChange={(event) =>
                                    setInteractionFilter(event.target.value)
                                  }
                                >
                                  <option value="all">Todos</option>
                                  <option value="interacted">Já interagiram</option>
                                  <option value="pending">Sem interação registrada</option>
                                </Form.Control>
                                <Form.Text className="text-muted">
                                  Use para priorizar quem ainda não respondeu ou já interagiu recentemente.
                                </Form.Text>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>

                        <Card>
                          <Card.Body>
                            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                              <div className="mb-2 mb-md-0">
                                {selectedCount === 0 ? (
                                  <span className="text-muted">
                                    Nenhum lead selecionado. Utilize as caixas ao lado dos nomes para montar a lista e disparar automações.
                                  </span>
                                ) : (
                                  <span>
                                    <strong>{selectedCount}</strong> lead{selectedCount === 1 ? "" : "s"} selecionado{selectedCount === 1 ? "" : "s"}.
                                    <Button
                                      variant="link"
                                      className="p-0 ml-2 align-baseline"
                                      onClick={handleClearSelection}
                                    >
                                      Limpar seleção
                                    </Button>
                                  </span>
                                )}
                              </div>
                              <div className="d-flex flex-wrap align-items-end">
                                <Form.Group controlId="templateBatchSize" className="mr-3 mb-2">
                                  <Form.Label className="mb-1">Lote por job (50–5000)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={50}
                                    max={5000}
                                    value={templateBatchSize}
                                    onChange={(event) => setTemplateBatchSize(event.target.value)}
                                  />
                                  <Form.Text className="text-muted">
                                    Use 1000–2000 para reduzir jobs em disparos de 4k–100k leads.
                                  </Form.Text>
                                </Form.Group>
                                <div className="d-flex flex-wrap">
                                  <Button
                                    variant="outline-primary"
                                    className="mr-2 mb-2"
                                    disabled={selectedCount === 0 || bulkTemplateSending}
                                    onClick={handleBulkSendTemplate}
                                  >
                                    {bulkTemplateSending ? "Enviando templates..." : "Enviar template"}
                                  </Button>
                                  <Button
                                    variant="outline-dark"
                                    className="mr-2 mb-2"
                                    disabled={selectedCount === 0}
                                    onClick={handleOpenBulkCustomMessageModal}
                                  >
                                    Mensagem personalizada
                                  </Button>
                                  <Button
                                    variant="outline-success"
                                    className="mb-2"
                                    disabled={selectedCount === 0 || bulkPromotionSending}
                                    onClick={handleBulkSendPromotions}
                                  >
                                    {bulkPromotionSending ? "Disparando promoções..." : "Disparar promoções"}
                                  </Button>
                                </div>
                              </div>
                            </div>
                            <div className="table-responsive">
                              <Table hover responsive>
                                <thead>
                                  <tr>
                                    <th style={{ width: 48 }}>
                                      <Form.Check
                                        type="checkbox"
                                        id="select-all-leads"
                                        ref={selectAllRef}
                                        checked={allFilteredSelected}
                                        onChange={handleToggleSelectAll}
                                        disabled={filteredLeadIdSet.size === 0}
                                        aria-label="Selecionar todos os leads listados"
                                      />
                                    </th>
                                    <th>Lead</th>
                                    <th>Status</th>
                                    <th>Interesse registrado</th>
                                    <th>Preferências</th>
                                    <th>Última interação</th>
                                    <th>Envios</th>
                                    <th>Promoções vinculadas</th>
                                    <th className="text-right">Ações</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredLeads.length === 0 && (
                                    <tr>
                                      <td colSpan={9} className="text-center text-muted py-4">
                                        Nenhum lead encontrado com os filtros selecionados.
                                      </td>
                                    </tr>
                                  )}
                                  {filteredLeads.map((lead) => (
                                    <tr key={lead.id}>
                                      <td className="align-middle" style={{ width: 48 }}>
                                        <Form.Check
                                          type="checkbox"
                                          id={`lead-select-${lead.id}`}
                                          checked={selectedLeadIds.includes(lead.id)}
                                          onChange={() => toggleLeadSelection(lead.id)}
                                          aria-label={`Selecionar ${lead.name || "lead"}`}
                                        />
                                      </td>
                                      <td className="align-middle">
                                        <strong>{lead.name}</strong>
                                      </td>
                                      <td className="align-middle">
                                        <div className="d-flex flex-column">
                                          <Badge variant={lead.interesse ? "success" : "secondary"}>
                                            {lead.interesse ? "Interessado" : "Sem interesse"}
                                          </Badge>
                                          <Badge
                                            variant={lead.blacklist ? "danger" : "light"}
                                            className="mt-1"
                                          >
                                            {lead.blacklist ? "Blacklist" : "Liberado"}
                                          </Badge>
                                        </div>
                                      </td>
                                      <td className="align-middle" style={{ minWidth: 220 }}>
                                        {renderLeadInterest(lead)}
                                      </td>
                                      <td className="align-middle" style={{ minWidth: 240 }}>
                                        {renderLeadPreferences(lead)}
                                      </td>
                                      <td className="align-middle">
                                        {formatDateTime(lead.last_interaction_at)}
                                      </td>
                                      <td className="align-middle" style={{ minWidth: 200 }}>
                                        {renderMessageStatus(lead)}
                                      </td>
                                      <td className="align-middle" style={{ minWidth: 220 }}>
                                        {(lead.promocoes || []).length > 0 ? (
                                          lead.promocoes.map((promo) => (
                                            <Badge
                                              key={promo.id}
                                              variant="info"
                                              className="mr-1 mb-1"
                                            >
                                              {promo.nome}
                                            </Badge>
                                          ))
                                        ) : (
                                          <span className="text-muted">Sem promoções</span>
                                        )}
                                      </td>
                                      <td className="align-middle text-right" style={{ minWidth: 200 }}>
                                        <Button
                                          variant="link"
                                          className="p-0 mr-3"
                                          onClick={() => {
                                            setEditingLead(lead);
                                            setShowModal(true);
                                          }}
                                        >
                                          Editar
                                        </Button>
                                        <Button
                                          variant="link"
                                          className="p-0 mr-3"
                                          onClick={() => handleSendTemplate(lead.id)}
                                        >
                                          Enviar template
                                        </Button>
                                        <Button
                                          variant="link"
                                          className="p-0 mr-3"
                                          disabled={lead.blacklist}
                                          title={
                                            lead.blacklist
                                              ? "Lead na blacklist. Remova-o para enviar mensagens."
                                              : undefined
                                          }
                                          onClick={() => openCustomMessageModalForLead(lead)}
                                        >
                                          Mensagem personalizada
                                        </Button>
                                        <Button
                                          variant="link"
                                          className="p-0 mr-3"
                                          onClick={() => handleSendPromotions(lead.id)}
                                        >
                                          Disparar promoções
                                        </Button>
                                        <Button
                                          variant="link"
                                          className="text-danger p-0"
                                          onClick={() => handleDeleteLead(lead.id)}
                                        >
                                          Remover
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Card.Body>
                        </Card>

                        <ImportLeadsModal
                          show={showImportModal}
                          onHide={handleCloseImportModal}
                          onSubmit={handleImportLeadsFromList}
                          loading={importingLeads}
                        />
                        <LeadModal
                          show={showModal}
                          onHide={handleCloseModal}
                          onSubmit={handleSubmitLead}
                          saving={savingLead}
                          initialData={editingLead}
                          promotions={promotions}
                        />
                        <CustomMessageModal
                          show={customMessageModal.open}
                          onHide={handleCloseCustomMessageModal}
                          onSubmit={handleSubmitCustomMessage}
                          sending={customMessageSending}
                          leadCount={customMessageModal.leadIds.length}
                          targetLabel={customMessageModal.contextLabel}
                        />
                      </>
                    ) : (
                      <Card>
                        <Card.Body>
                          <h5 className="mb-2">Lista restrita</h5>
                          <p className="text-muted mb-0">
                            Somente administradores conseguem editar informações e disparar mensagens. Utilize as visões de interesse para
                            acompanhar as respostas e compartilhe necessidades com a equipe responsável.
                          </p>
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </Container>

  );
}